import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { Typography, Tabs, TabPanel, Spacer, useSnackbar, Stack, Checkbox, FormControlLabel } from '@emburse/embark-core';
import { useDialog } from '../../components/Dialog';
import useIdentityVerification from '../../hooks/useIdentityVerification';
import { $t, useTranslation } from '../../hooks/useTranslation';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import { getNOCsForCurrentUser, acknowledgeNOCsForCurrentUser, getNOCDetails } from '../../api/noc';
import NOCTable from './NOCTable';
import useAccountConfig from '../../hooks/useAccountConfig';
var TabOptions;
(function (TabOptions) {
    TabOptions[TabOptions["MyBankAccount"] = 0] = "MyBankAccount";
    TabOptions[TabOptions["InvoiceVendor"] = 1] = "InvoiceVendor";
})(TabOptions || (TabOptions = {}));
function NOC() {
    useTranslation();
    const { accountConfig } = useAccountConfig();
    const [loading, setLoading] = useState(true);
    const [myBankAccountData, setMyBankAccountData] = useState([]);
    const [invoiceVendorData, setInvoiceVendorData] = useState([]);
    const [tab, setTab] = useState(0);
    const [hasConsentedToUnmask, sethasConsentedToUnmask] = useState(false);
    const isAdmin = accountConfig.UIFlags.EmpAdminLevel === 'Full';
    const { UseInvoice } = accountConfig.UIFlags;
    const showInvoiceVendorsTab = isAdmin && UseInvoice;
    const { requireIdentityVerification } = useIdentityVerification();
    const tabs = [
        { label: $t('My Bank Account') },
        ...(showInvoiceVendorsTab ? [{ label: $t('Invoice Vendors') }] : [])
    ];
    const { enqueueSnackbar } = useSnackbar();
    const { enqueueDialog } = useDialog();
    const handleError = useCallback((err) => {
        enqueueSnackbar(err.message, { variant: 'alert', AlertProps: { severity: 'error' } });
    }, [enqueueSnackbar]);
    const acknowledgeNOCs = useCallback((booAcknowledgeNOCs) => {
        // Acknowledge any NOCS that were not previously viewed
        if (booAcknowledgeNOCs) {
            acknowledgeNOCsForCurrentUser().catch(handleError);
        }
    }, [handleError]);
    useEffect(() => {
        setLoading(true);
        getNOCsForCurrentUser()
            .then((resp) => {
            if (resp.Status !== 'success') {
                throw new Error(resp.Message);
            }
            const data = JSON.parse(resp.Data);
            const acknowledgementRequired = data.filter((noc) => !noc.Acknowledged).length > 0;
            setMyBankAccountData(data.filter((row) => !row.IsInvoiceVendor));
            if (showInvoiceVendorsTab) {
                setInvoiceVendorData(data.filter((row) => row.IsInvoiceVendor));
            }
            acknowledgeNOCs(acknowledgementRequired);
        })
            .catch(handleError)
            .finally(() => {
            setLoading(false);
        });
    }, [handleError, showInvoiceVendorsTab, acknowledgeNOCs]);
    const revealSensitiveNOCDetails = (nocID) => {
        setLoading(true);
        // pull sensitive data to reveal in table
        getNOCDetails(nocID)
            .then((resp) => {
            if (resp.Status !== 'success') {
                throw new Error(resp.Message);
            }
            const nocData = JSON.parse(resp.Data);
            if (!nocData.IsInvoiceVendor) {
                setMyBankAccountData([
                    ...myBankAccountData.map((row) => row.PK === nocID ? { ...row, UnMaskedData: nocData.Data } : row)
                ]);
            }
            else {
                setInvoiceVendorData([
                    ...invoiceVendorData.map((row) => row.PK === nocID ? { ...row, UnMaskedData: nocData.Data } : row)
                ]);
            }
            setLoading(false);
        })
            .catch(handleError);
    };
    const doNotRequestConsentHandler = (e) => {
        sethasConsentedToUnmask(e.target.checked);
    };
    function cautionDialogContent() {
        return (_jsxs(Stack, { spacing: 1, alignItems: "flex-start", children: [_jsx(Typography, { children: $t(`You are about to display sensitive banking information. Please exercise caution if you are in a public area.
                            Are you sure you want to continue?`) }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { id: "doNotRequestConfirmation", color: "primary", onChange: doNotRequestConsentHandler }), label: $t("Don't ask me again on this page."), labelPlacement: "end" })] }));
    }
    const checkForhasConsentedToUnmask = (nocID) => {
        requireIdentityVerification(() => {
            if (!hasConsentedToUnmask) {
                const cautionDialog = {
                    title: $t('Caution'),
                    show: true,
                    onConfirm: () => revealSensitiveNOCDetails(nocID),
                    content: cautionDialogContent(),
                    confirmText: $t('Continue'),
                    color: 'warning'
                };
                enqueueDialog(cautionDialog);
            }
            else {
                revealSensitiveNOCDetails(nocID);
            }
        });
    };
    const maskSensitiveNOCDetails = (nocID) => {
        setLoading(true);
        const updateTable = tab === TabOptions.MyBankAccount ? myBankAccountData : invoiceVendorData;
        const updateNOC = updateTable.find((row) => row.PK === nocID);
        updateNOC.UnMaskedData = null;
        if (!updateNOC.IsInvoiceVendor) {
            setMyBankAccountData([
                ...myBankAccountData.map((row) => (row.PK === nocID ? updateNOC : row))
            ]);
        }
        else {
            setInvoiceVendorData([
                ...invoiceVendorData.map((row) => (row.PK === nocID ? updateNOC : row))
            ]);
        }
        setLoading(false);
    };
    const onUnmaskHandler = ({ row }) => {
        if (!row.masked) {
            maskSensitiveNOCDetails(row.id);
        }
        else {
            checkForhasConsentedToUnmask(row.id);
        }
    };
    const tabsChangeHandler = (e, newTabIndex) => {
        setTab(newTabIndex);
    };
    return (_jsx(SingleColumnLayout, { children: _jsxs(Stack, { spacing: 1, alignItems: "flex-start", children: [_jsx(Typography, { variant: "h4", gutterBottom: true, children: $t('Automated ACH Account Update History') }), _jsx(Spacer, { size: 3 }), _jsx(Stack, { spacing: 4, children: _jsx(Tabs, { id: "noc-tabs", orientation: "horizontal", tabs: tabs, onChange: tabsChangeHandler }) }), _jsx(TabPanel, { value: tab, index: TabOptions.MyBankAccount, style: { width: '100%' }, children: _jsx(NOCTable, { loading: loading, rows: myBankAccountData, columns: [], onUnmask: onUnmaskHandler }) }), _jsx(TabPanel, { value: tab, index: TabOptions.InvoiceVendor, style: { width: '100%' }, children: _jsx(NOCTable, { loading: loading, rows: invoiceVendorData, columns: [], onUnmask: onUnmaskHandler }) })] }) }));
}
export default NOC;
